import { useNavigate } from "react-router-dom";
import { S3_BASE_URL } from "src/constants/endpoints";
import RestuarantImg from "src/assests/images/dashboard/default-thumbnail (1).jpg";
import type { MenuCategory } from "src/interfaces/menu.interface";
import { restaurantSegment } from "src/utils/restaurantUrl";

interface MenuCategoryCardProps {
  menuCategory: MenuCategory;
  restaurantCorrelationId: string;
  restaurantName?: string;
  onEdit: (category: MenuCategory) => void;
  onDelete: (categoryId: string) => void;
}

export default function MenuCategoryCard({
  menuCategory,
  restaurantCorrelationId,
  restaurantName,
  onEdit,
  onDelete,
}: MenuCategoryCardProps) {
  const navigate = useNavigate();

  const segment = restaurantSegment(restaurantCorrelationId, restaurantName);
  const itemCount = menuCategory?.items?.length ?? 0;
  const isInactive = menuCategory.active === false;
  const imageSrc = menuCategory?.bucketKeyName
    ? menuCategory.bucketKeyName.includes("blob")
      ? menuCategory.bucketKeyName
      : S3_BASE_URL + menuCategory.bucketKeyName
    : RestuarantImg;

  const handleCardClick = () => {
    navigate(`/restaurants/${segment}/menu/categories/${menuCategory.correlationId}`);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(menuCategory);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(menuCategory.correlationId);
  };

  return (
    <div
      className="w-full cursor-pointer group"
      onClick={handleCardClick}
      onKeyDown={(e) => e.key === "Enter" && handleCardClick()}
      role="button"
      tabIndex={0}
      aria-label={`Open ${menuCategory?.name}, ${itemCount} items`}
    >
      <div
        className={`
          relative w-full flex flex-col
          border border-black/[0.05] bg-[#FFFFFF] rounded-xl
          shadow-[0_4px_12px_rgba(0,0,0,0.04)]
          transition-all duration-[200ms] ease-out
          hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] hover:border-black/[0.08] hover:-translate-y-[2px]
          active:scale-[0.99]
          ${isInactive ? "opacity-[0.88]" : ""}
        `}
      >
        {/* Image area — tinted placeholder, subtle bottom gradient overlay */}
        <div className="relative flex justify-center bg-[#EEF0F2] h-[99px] rounded-t-xl overflow-hidden shrink-0 border-b border-black/[0.05]">
          <img
            alt=""
            className={`w-full h-full object-cover transition-[filter] duration-200 group-hover:brightness-[1.02] ${isInactive ? "grayscale-[0.35]" : ""}`}
            src={imageSrc}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: "linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.03))" }}
            aria-hidden
          />
        </div>

        {/* Content */}
        <div className="px-4 pt-3 pb-3">
          <div className="flex items-start justify-between gap-3 min-w-0">
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-[#111] truncate leading-tight" style={{ fontWeight: 600 }}>
                {menuCategory?.name}
              </h2>
              <p className="mt-1 text-xs text-[#6B7280] leading-none">
                {itemCount === 1 ? "1 item" : `${itemCount} items`}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                className="inline-flex h-6 items-center rounded-md px-2 text-[12px] font-semibold leading-none text-[#FD7657] transition-colors hover:bg-[#FFF0EB] hover:text-[#E85C32] focus:outline-none focus:ring-2 focus:ring-[#FD7657]/20"
                onClick={handleEdit}
              >
                Edit
              </button>
              <button
                type="button"
                className="inline-flex h-6 w-6 items-center justify-center rounded-md text-[#9CA3AF] transition-colors hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-200"
                aria-label={`Delete ${menuCategory?.name}`}
                title="Delete category"
                onClick={handleDelete}
              >
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166M19.228 5.79L18.16 19.673A2.25 2.25 0 0115.916 21.75H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .563c.34-.059.68-.114 1.022-.166m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                </svg>
              </button>
            </div>
          </div>
          {isInactive ? (
            <div className="mt-2">
              <span className="inline-flex items-center w-fit text-[11px] text-[#92400E] font-medium rounded-full border border-[#FCD34D] bg-[#FFFBEB] py-0.5 px-2">
                Inactive
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
